/*
 * Saved lists — Wishlist + Favourites (two independent lists)
 * Pattern mirrors assets/recently-viewed.js: Alpine store + localStorage.
 *
 * Data model: two named lists, each a JSON array of product IDs in localStorage.
 *   wishlist   -> localStorage 'eurus_wishlist'    (+ customer metafield custom.wishlist)
 *   favourites -> localStorage 'eurus_favourites'  (+ customer metafield custom.favourites)
 *
 * Guests: localStorage is the source of truth (works with zero setup).
 * Logged-in READ: window.Eurus.savedSeed is server-rendered from the customer
 *   metafields in layout/theme.liquid and merged on init().
 * Logged-in WRITE (cross-device sync): set SYNC_ENDPOINT to an app-proxy path
 *   (e.g. '/apps/saved') that writes the customer metafields via the Admin API.
 *   While SYNC_ENDPOINT is null the lists are localStorage-only.
 */
if (!window.Eurus.loadedScript.has('saved-lists.js')) {
  window.Eurus.loadedScript.add('saved-lists.js');

  const SAVED_LISTS = {
    wishlist:   { storageKey: 'eurus_wishlist',   metafield: 'wishlist' },
    favourites: { storageKey: 'eurus_favourites', metafield: 'favourites' }
  };
  // Account write-sync endpoint (app proxy). Null => localStorage only.
  const SYNC_ENDPOINT = null;

  const readLS = (key) => {
    try {
      const arr = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(arr) ? arr.map(Number).filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  };
  const writeLS = (key, ids) => {
    try { localStorage.setItem(key, JSON.stringify(ids)); } catch (e) {}
  };

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.store('xSaved', {
        lists: { wishlist: [], favourites: [] },

        init() {
          Object.keys(SAVED_LISTS).forEach((name) => {
            this.lists[name] = readLS(SAVED_LISTS[name].storageKey);
          });
          // Merge logged-in customer's saved IDs (server-rendered) into local lists.
          const seed = (window.Eurus && window.Eurus.savedSeed) || null;
          if (seed) {
            Object.keys(SAVED_LISTS).forEach((name) => {
              const serverIds = Array.isArray(seed[name]) ? seed[name].map(Number).filter(Boolean) : [];
              if (serverIds.length) {
                const merged = [...new Set([...serverIds, ...this.lists[name]])];
                this.lists[name] = merged;
                writeLS(SAVED_LISTS[name].storageKey, merged);
              }
            });
          }
        },

        ids(name) { return this.lists[name] || []; },
        has(name, id) { return (this.lists[name] || []).includes(Number(id)); },
        count(name) { return (this.lists[name] || []).length; },

        toggle(name, id) {
          id = Number(id);
          if (!SAVED_LISTS[name] || !id) return;
          const current = this.lists[name] || [];
          // Reassign (not push/splice) so Alpine reactivity fires on cards + header.
          this.lists[name] = current.includes(id)
            ? current.filter((x) => x !== id)
            : [id, ...current];
          this._persist(name);
          document.dispatchEvent(new CustomEvent('eurus:saved:change', {
            detail: { list: name, id: id, ids: this.lists[name] }
          }));
        },

        clear(name) {
          if (!SAVED_LISTS[name]) return;
          this.lists[name] = [];
          this._persist(name);
        },

        _persist(name) {
          const cfg = SAVED_LISTS[name];
          writeLS(cfg.storageKey, this.lists[name]);
          // Pluggable account write-sync (requires app proxy / Customer Account API).
          if (SYNC_ENDPOINT && window.Eurus && window.Eurus.customerLoggedIn) {
            try {
              fetch(SYNC_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ metafield: cfg.metafield, ids: this.lists[name] })
              }).catch(() => {});
            } catch (e) {}
          }
        },

        // Renders the saved products onto the wishlist/favourites page.
        // sectionId is the full template-qualified section id ({{ section.id }});
        // name is 'wishlist' or 'favourites'.
        renderPage(sectionId, name) {
          const ids = this.ids(name);
          const wrap = document.getElementById('x-saved-inner-' + name);
          const empty = document.getElementById('x-saved-empty-' + name);
          const loader = document.getElementById('x-saved-loading-' + name);
          const done = (showEmpty) => {
            if (loader) loader.classList.add('hidden');
            if (showEmpty && empty) empty.classList.remove('hidden');
          };
          if (!ids.length) { done(true); return; }
          const query = ids.map((v) => 'id:' + v).join(' OR ');
          const url = Shopify.routes.root + 'search?section_id=' + encodeURIComponent(sectionId) +
            '&type=product&q=' + encodeURIComponent(query);
          fetch(url)
            .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
            .then((text) => {
              const doc = new DOMParser().parseFromString(text, 'text/html');
              const fresh = doc.getElementById('x-saved-inner-' + name);
              if (fresh && fresh.innerHTML.trim().length && wrap) {
                wrap.innerHTML = fresh.innerHTML;
                done(false);
              } else {
                done(true);
              }
            })
            .catch(() => done(true));
        }
      });
    });
  });
}
